import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Crown } from "lucide-react";

export default function Subscription() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get("/settings/public").then((r) => setSettings(r.data)); }, []);

    const upgrade = async () => {
        setBusy(true);
        try {
            await api.post("/subscription/mock-checkout");
            await refresh();
            toast.success("Prepcheck Plus is active. Unlimited AI analytics unlocked.");
            nav("/dashboard");
        } catch (e) { toast.error("Checkout failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
            <span className="overline text-primary">▮ Subscription</span>
            <h1 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tighter">
                Unlock unlimited <span className="text-primary">AI analytics</span>
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
                Practice, scoring and generation stay free forever. Prepcheck Plus unlocks the AI coach: weakness diagnosis, 7-day plans, and readiness scoring.
            </p>

            <div className="mt-10 grid md:grid-cols-2 gap-4">
                <PriceCard
                    title="Free"
                    price={`${settings?.currency === "INR" ? "₹" : "$"}0`}
                    features={[
                        "Unlimited question generation",
                        `First ${settings?.free_ai_analytics_limit || 15} AI reports free`,
                        "Full NTA-style exam UI",
                        "History & scoring",
                    ]}
                    active={!user?.is_subscribed}
                />
                <PriceCard
                    title="Prepcheck Plus"
                    highlight
                    price={`${settings?.currency === "INR" ? "₹" : "$"}${settings?.subscription_price_monthly || 299}`}
                    period="/month"
                    features={[
                        "Unlimited AI analytics",
                        "Personal 7-day study plan",
                        "Readiness scoring",
                        "Priority worker queue",
                    ]}
                    active={user?.is_subscribed}
                    cta={
                        user?.is_subscribed ? (
                            <Button disabled className="w-full mt-6 rounded-sm">Active</Button>
                        ) : (
                            <Button data-testid="subscription-upgrade-btn" onClick={upgrade} disabled={busy}
                                className="w-full mt-6 rounded-sm bg-primary hover:bg-primary/90 btn-press">
                                <Crown className="w-4 h-4 mr-2" />{busy ? "Processing…" : "Upgrade (mock)"}
                            </Button>
                        )
                    }
                />
            </div>

            <p className="mt-6 text-xs text-muted-foreground font-mono">
                ▮ Payments are currently MOCKED. In production, integrate Stripe / Razorpay from the admin dashboard.
            </p>
        </div>
    );
}

const PriceCard = ({ title, price, period, features, highlight, active, cta }) => (
    <div className={`border ${highlight ? "border-2 border-primary bg-primary/5" : "border-border"} rounded-sm p-8 relative`}>
        {active && <span className="absolute -top-3 left-6 bg-green-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm">Your plan</span>}
        <div className="overline text-muted-foreground">{title}</div>
        <div className="mt-2 font-heading font-black text-5xl tracking-tighter">
            {price}<span className="text-base font-normal text-muted-foreground">{period}</span>
        </div>
        <ul className="mt-6 space-y-2 text-sm">
            {features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                </li>
            ))}
        </ul>
        {cta}
    </div>
);
